// ============================================================================
// Umgebungsspeicher (R6-02-a, Diagnose C2-R6-02).
// ----------------------------------------------------------------------------
// Die API-Basis ist zur BAUZEIT eingefroren (VITE_API_BASE in 21 Modulen), die
// Beta-Umgebung wird deshalb NICHT durch Umbiegen von Adressen erreicht,
// sondern durch das Laden des Beta-Bundles ueber OTA — dieses Bundle traegt
// beta.lorath.eu und den Kanal beta bereits eingebacken.
//
// Zwei streng getrennte Begriffe:
//   * WUNSCH  (`ff_env`)  — welche Umgebung soll beim naechsten OTA-Lauf
//     geladen werden. Wird hier doppelt gespeichert: clientPrefs (Cookie +
//     localStorage, SYNCHRON lesbar) UND Capacitor Preferences (ueberlebt den
//     iOS-Kaltstart und den Bundle-Tausch zuverlaessig, weil der WebView-
//     Ursprung beim Wechsel gleich bleibt).
//   * WAHRHEIT (`runningEnv()`) — welches Bundle GERADE laeuft. Alles, was die
//     Wahrheit anzeigt oder absichert (Wasserzeichen, Token-/Datenbank-
//     Trennung, Kaufsperre, tiefe Verweise), haengt an runningEnv(), NIE am
//     Wunsch — sonst behauptet die App „Beta", waehrend noch Prod-Code laeuft.
//
// `ff_env_force` ist ein einmaliges Kennzeichen fuer den ERSTEN Check nach dem
// Umschalten: dort geht `bundle=''` an den Server, damit dessen Downgrade-
// Sperre (server/repositories/ota-repository.js:137-139) nicht greift, falls
// die Zielumgebung gerade niedriger nummeriert ist.
// ============================================================================

import { Capacitor } from '@capacitor/core';
import { Preferences } from '@capacitor/preferences';
import { clearPref, readPref, writePref } from '../shared/clientPrefs';
import { BETA_BASE_URL, PROD_BASE_URL, detectIsBetaEnvironment } from '../pages/settings/betaSwitch';

export type AppEnv = 'production' | 'beta';

export const ENV_PREF_KEY = 'ff_env';
export const ENV_FORCE_PREF_KEY = 'ff_env_force';
const ENV_FORCE_VALUE = '1';

/** Nur die beiden bekannten Werte zaehlen; alles andere = kein Wunsch. */
export function normalizeAppEnv(raw: string | null | undefined): AppEnv | null {
  if (raw === 'beta') return 'beta';
  if (raw === 'production') return 'production';
  return null;
}

/** Die API-/OTA-Basis der jeweiligen Umgebung. */
export function apiBaseForEnv(env: AppEnv): string {
  return env === 'beta' ? BETA_BASE_URL : PROD_BASE_URL;
}

/** Die jeweils ANDERE Umgebung (fuer Umschalter und Verweis-Weiche). */
export function otherEnv(env: AppEnv): AppEnv {
  return env === 'beta' ? 'production' : 'beta';
}

// ─── Wahrheit: welches Bundle laeuft gerade? ─────────────────────────────────

/**
 * Welches Bundle laeuft GERADE? Stuetzt sich auf den Bestand
 * (detectIsBetaEnvironment): `<html data-app-env>` im Web, der Hostname und —
 * im nativen Build entscheidend — die einkompilierte API-Basis. Web-Beta
 * (beta.lorath.eu im Browser) zaehlt bewusst ebenfalls als 'beta'.
 */
export function runningEnv(): AppEnv {
  let appEnv: string | null = null;
  let hostname: string | null = null;
  try {
    appEnv = typeof document === 'undefined' ? null : document.documentElement.dataset.appEnv ?? null;
  } catch {
    appEnv = null;
  }
  try {
    hostname = typeof window === 'undefined' ? null : window.location.hostname;
  } catch {
    hostname = null;
  }
  const apiBase = import.meta.env.VITE_API_BASE ?? null;
  return detectIsBetaEnvironment({ appEnv, hostname, apiBase }) ? 'beta' : 'production';
}

// ─── Speicher A: clientPrefs (Cookie + localStorage, synchron) ───────────────

function readPrefSafe(name: string): string | null {
  try {
    return readPref(name);
  } catch {
    // Privatmodus / kein document — dann zaehlt der Speicher als leer.
    return null;
  }
}

function writePrefSafe(name: string, value: string): void {
  try {
    writePref(name, value);
  } catch {
    // Gesperrter Speicher darf den Umschalter nicht werfen lassen.
  }
}

function clearPrefSafe(name: string): void {
  try {
    clearPref(name);
  } catch {
    // s.o.
  }
}

// ─── Speicher B: Capacitor Preferences (nativ, ueberlebt Kaltstart) ──────────

type PreferencesApi = typeof Preferences;

/**
 * SYNCHRON — das Plugin-Objekt darf NIE aus einer async-Funktion zurueckgegeben
 * werden: Capacitors Proxy beantwortet jeden Property-Zugriff als Methode, auch
 * `.then` (siehe auth/storage.ts). Sync zurueckgeben umgeht das komplett.
 */
function nativePreferences(): PreferencesApi | null {
  try {
    if (Capacitor?.isNativePlatform?.() !== true) return null;
    if (Capacitor?.isPluginAvailable?.('Preferences') !== true) return null;
    return Preferences;
  } catch {
    return null;
  }
}

async function nativeGet(key: string): Promise<string | null> {
  const prefs = nativePreferences();
  if (!prefs) return null;
  try {
    const result = await prefs.get({ key });
    return result?.value ?? null;
  } catch {
    return null;
  }
}

async function nativeSet(key: string, value: string): Promise<void> {
  const prefs = nativePreferences();
  if (!prefs) return;
  try {
    await prefs.set({ key, value });
  } catch {
    // Best-effort: der synchrone Speicher traegt den Wunsch bereits.
  }
}

async function nativeRemove(key: string): Promise<void> {
  const prefs = nativePreferences();
  if (!prefs) return;
  try {
    await prefs.remove({ key });
  } catch {
    // s.o.
  }
}

// ─── Wunsch lesen/schreiben ──────────────────────────────────────────────────

/** Synchron lesbarer Wunsch (Cookie zuerst, dann localStorage). */
export function readEnvOverride(): AppEnv | null {
  return normalizeAppEnv(readPrefSafe(ENV_PREF_KEY));
}

/** Schreibt den Wunsch in BEIDE Speicher. */
export async function writeEnvOverride(env: AppEnv): Promise<void> {
  writePrefSafe(ENV_PREF_KEY, env);
  await nativeSet(ENV_PREF_KEY, env);
}

/** Loescht den Wunsch aus BEIDEN Speichern. */
export async function clearEnvOverride(): Promise<void> {
  clearPrefSafe(ENV_PREF_KEY);
  await nativeRemove(ENV_PREF_KEY);
}

/** Einmal-Kennzeichen fuer den ersten Check nach dem Umschalten. */
export function readEnvForce(): boolean {
  return readPrefSafe(ENV_FORCE_PREF_KEY) === ENV_FORCE_VALUE;
}

export async function setEnvForce(): Promise<void> {
  writePrefSafe(ENV_FORCE_PREF_KEY, ENV_FORCE_VALUE);
  await nativeSet(ENV_FORCE_PREF_KEY, ENV_FORCE_VALUE);
}

export async function clearEnvForce(): Promise<void> {
  clearPrefSafe(ENV_FORCE_PREF_KEY);
  await nativeRemove(ENV_FORCE_PREF_KEY);
}

export interface EnvState {
  env: AppEnv | null;
  force: boolean;
}

/**
 * Liest den nativen Speicher (die verlaesslichere Quelle nach Kaltstart und
 * Bundle-Tausch) und spiegelt ihn in die synchron lesbaren Speicher zurueck.
 * Ohne natives Plugin (Web) faellt sie auf den synchronen Stand zurueck.
 */
export async function hydrateEnvState(): Promise<EnvState> {
  if (!nativePreferences()) {
    return { env: readEnvOverride(), force: readEnvForce() };
  }

  const [storedEnv, storedForce] = await Promise.all([
    nativeGet(ENV_PREF_KEY),
    nativeGet(ENV_FORCE_PREF_KEY),
  ]);

  const env = normalizeAppEnv(storedEnv);
  if (env) writePrefSafe(ENV_PREF_KEY, env);
  else clearPrefSafe(ENV_PREF_KEY);

  const force = storedForce === ENV_FORCE_VALUE;
  if (force) writePrefSafe(ENV_FORCE_PREF_KEY, ENV_FORCE_VALUE);
  else clearPrefSafe(ENV_FORCE_PREF_KEY);

  return { env, force };
}
